import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import busStations from "../../../Data/BusStations.json";
import {
  RiCalendarLine,
  RiMapPinLine,
  RiMapPinFill,
  RiSearchLine,
  RiSwapLine,
  RiLeafLine,
} from "react-icons/ri";
import styles from "../SearchBusPanel/SearchBusPanel2.module.css";

const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
  <div onClick={onClick} ref={ref} className={styles.dateInputContainer}>
    <RiCalendarLine className={styles.calendarIcon} />
    <div className={styles.dateStack}>
      <span className={styles.dateHint}>Journey Date</span>
      <span className={`${styles.dateVal} ${value ? styles.dateSelected : ""}`}>
        {value || "Select date"}
      </span>
    </div>
  </div>
));

const SearchBusPanel2 = ({ onSearch, mode, onNavigate, templateId }) => {
  const navigate = useNavigate();

  const [fromStation, setFromStation] = useState("HYDERABAD");
  const [toStation, setToStation] = useState("BANGALORE");
  const [fromStationId, setFromStationId] = useState("");
  const [toStationId, setToStationId] = useState("");
  const [travelDate, setTravelDate] = useState(new Date());
  const [errors, setErrors] = useState({});
  const [activeField, setActiveField] = useState(null);
  const [fromStationResults, setFromStationResults] = useState([]);
  const [toStationResults, setToStationResults] = useState([]);

  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  const handleSwapStations = () => {
    setFromStation(toStation);
    setToStation(fromStation);
    setFromStationId(toStationId);
    setToStationId(fromStationId);
  };

  const handleFromStationSearch = (e) => {
    const value = e.target.value;
    setFromStation(value);
    if (value.length > 0) {
      setFromStationResults(
        busStations.stationList
          .filter((s) => s.stationName.toLowerCase().includes(value.toLowerCase()))
          .slice(0, 8)
      );
    } else {
      setFromStationResults([]);
    }
  };

  const handleToStationSearch = (e) => {
    const value = e.target.value;
    setToStation(value);
    if (value.length > 0) {
      setToStationResults(
        busStations.stationList
          .filter((s) => s.stationName.toLowerCase().includes(value.toLowerCase()))
          .slice(0, 8)
      );
    } else {
      setToStationResults([]);
    }
  };

  const handleFromStationSelect = (station) => {
    setFromStation(station.stationName);
    setFromStationId(station.stationId);
    setFromStationResults([]);
    setActiveField(null);
  };

  const handleToStationSelect = (station) => {
    setToStation(station.stationName);
    setToStationId(station.stationId);
    setToStationResults([]);
    setActiveField(null);
  };

  const validate = () => {
    const temp = {
      fromStation: fromStation !== "",
      toStation: toStation !== "",
      travelDate: travelDate !== null,
      sameStation: fromStation !== toStation,
    };
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (validate()) {
      const searchQuery = {
        fromStationCode: fromStationId,
        fromStation,
        toStationCode: toStationId,
        toStation,
        travelDate: moment(travelDate).format("YYYY-MM-DD"),
      };
      if (mode === "builder") {
        onNavigate({ name: "bus-results", params: { searchQuery } });
      } else if (mode === "preview") {
        navigate(`/template-preview/${templateId}/bus-results`, { state: { searchQuery } });
      } else {
        onSearch(searchQuery);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(`.${styles.stationDropdown}`)) {
        setFromStationResults([]);
        setToStationResults([]);
        setActiveField(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.cardHeader}>
          <RiLeafLine className={styles.leafIcon} />
          <span className={styles.brandText}>BUS TRAVEL</span>
          <span className={styles.brandDot}>·</span>
          <span className={styles.brandSub}>Premium Routes Across India</span>
        </div>

        {/* Single horizontal row */}
        <div className={styles.row}>

          {/* FROM */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>ORIGIN</span>
            <div className={`${styles.fieldBox} ${activeField === "from" ? styles.fieldActive : ""} ${errors.fromStation === false ? styles.fieldErr : ""}`}>
              <RiMapPinLine className={styles.iconFrom} />
              <input
                ref={fromInputRef}
                type="text"
                placeholder="Departure city"
                value={fromStation}
                onChange={handleFromStationSearch}
                onFocus={() => {
                  setActiveField("from");
                  setFromStationResults(busStations.stationList.slice(0, 8));
                }}
                className={styles.input}
              />
            </div>
            {fromStationResults.length > 0 && activeField === "from" && (
              <ul className={`${styles.dropdown} ${styles.stationDropdown}`}>
                {fromStationResults.map((s) => (
                  <li key={s.stationId} onClick={() => handleFromStationSelect(s)} className={styles.dropItem}>
                    <RiMapPinLine className={styles.dropIcon} />
                    <span>{s.stationName}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* SWAP */}
          <button type="button" className={styles.swapBtn} onClick={handleSwapStations} aria-label="Swap stations">
            <RiSwapLine />
          </button>

          {/* TO */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>DESTINATION</span>
            <div className={`${styles.fieldBox} ${activeField === "to" ? styles.fieldActive : ""} ${errors.toStation === false ? styles.fieldErr : ""}`}>
              <RiMapPinFill className={styles.iconTo} />
              <input
                ref={toInputRef}
                type="text"
                placeholder="Arrival city"
                value={toStation}
                onChange={handleToStationSearch}
                onFocus={() => {
                  setActiveField("to");
                  setToStationResults(busStations.stationList.slice(0, 8));
                }}
                className={styles.input}
              />
            </div>
            {toStationResults.length > 0 && activeField === "to" && (
              <ul className={`${styles.dropdown} ${styles.stationDropdown}`}>
                {toStationResults.map((s) => (
                  <li key={s.stationId} onClick={() => handleToStationSelect(s)} className={styles.dropItem}>
                    <RiMapPinFill className={styles.dropIcon} />
                    <span>{s.stationName}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* DIVIDER */}
          <div className={styles.rule} />

          {/* DATE */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>DATE</span>
            <div className={`${styles.fieldBox} ${errors.travelDate === false ? styles.fieldErr : ""}`}>
              <DatePicker
                selected={travelDate}
                onChange={setTravelDate}
                customInput={<CustomDateInput />}
                dateFormat="dd-MMM-yyyy"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                minDate={new Date()}
                yearDropdownItemNumber={50}
                scrollableYearDropdown
                placeholderText="Select date"
              />
            </div>
          </div>

          {/* SEARCH BUTTON */}
          <button type="button" onClick={handleSearch} className={styles.searchBtn}>
            <RiSearchLine className={styles.searchIcon} />
            <span>Search Buses</span>
          </button>
        </div>

        {/* Errors */}
        {Object.values(errors).some((v) => v === false) && (
          <div className={styles.errors}>
            {errors.fromStation === false && <span className={styles.errChip}>⚠ Enter departure city</span>}
            {errors.toStation === false && <span className={styles.errChip}>⚠ Enter destination city</span>}
            {errors.travelDate === false && <span className={styles.errChip}>⚠ Choose a travel date</span>}
            {errors.sameStation === false && <span className={styles.errChip}>⚠ Origin & destination must differ</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBusPanel2;